import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { XCircle } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private isAuthError() {
    return (
      this.state.error?.message.includes('Authentication required') ||
      this.state.error?.message.includes('session has expired')
    );
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card className="max-w-md p-6">
            <div className="flex flex-col items-center text-center">
              <XCircle className="h-12 w-12 text-destructive" />
              <h1 className="mt-4 text-2xl font-bold">Oops, there was an error</h1>
              <p className="mt-2 text-muted-foreground">{this.state.error?.message}</p>
              <div className="mt-6 flex gap-4">
                {this.isAuthError() ? (
                  <Button asChild variant="default">
                    <Link to="/signin">Sign In</Link>
                  </Button>
                ) : (
                  <Button onClick={() => this.setState({ hasError: false, error: null })}>Try again</Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
